// One toast system for the whole app (Nav, 2026-10-05): green for done, orange for
// "worked, but", red for failed, indigo for info. Call toast.success("Saved") anywhere.
export type ToastKind = "success" | "warning" | "error" | "info";
export interface Toast {
  id: number;
  kind: ToastKind;
  text: string;
}

const LATER_KEY = "flp_toast_next";
let next = 1;
let items: Toast[] = [];
const subs = new Set<(t: Toast[]) => void>();
const emit = () => subs.forEach((f) => f(items));

export function dismiss(id: number): void {
  items = items.filter((t) => t.id !== id);
  emit();
}

function show(kind: ToastKind, text: string): void {
  const id = next++;
  // The same message twice in a row replaces itself instead of stacking.
  items = [...items.filter((t) => t.text !== text), { id, kind, text }].slice(-3);
  emit();
  setTimeout(() => dismiss(id), kind === "error" ? 6000 : 3500);
}

export const toast = {
  success: (text: string) => show("success", text),
  warning: (text: string) => show("warning", text),
  error: (text: string) => show("error", text),
  info: (text: string) => show("info", text),
  /** Shows after the next page load (sign out, delete account and other actions that reload). */
  later(kind: ToastKind, text: string): void {
    try {
      sessionStorage.setItem(LATER_KEY, JSON.stringify({ kind, text }));
    } catch {
      // No storage: the action still happened, only the message is lost.
    }
  },
};

export function subscribe(f: (t: Toast[]) => void): () => void {
  subs.add(f);
  f(items);
  return () => subs.delete(f);
}

/** Shows a toast saved with toast.later() before the reload. */
export function flushLater(): void {
  try {
    const raw = sessionStorage.getItem(LATER_KEY);
    if (!raw) return;
    sessionStorage.removeItem(LATER_KEY);
    const { kind, text } = JSON.parse(raw) as { kind: ToastKind; text: string };
    show(kind, text);
  } catch {
    // Ignore a damaged entry.
  }
}
